<template>
  <div
    v-sight-snippet-view-tracker="analyticsParams"
    v-sight-snippet-click-tracker="analyticsParams"
    class="sight-wrap"
    role="button"
    tabindex="0"
    @click="$emit('select', attraction)"
    @keydown.enter="$emit('select', attraction)"
    @keydown.space.prevent="$emit('select', attraction)"
  >
    <div class="sight">
      <img v-if="thumbnail" :src="thumbnail" :alt="attraction.name" class="sight__img" />
      <div class="sight__info">
        <div class="sight__title">{{ attraction.name }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useAnalytics } from '@/shared/lib/analytics';

import type { NearbyAttraction } from './models';

const props = withDefaults(
  defineProps<{
    attraction: NearbyAttraction;
    analyticsParams?: Record<string, unknown>;
  }>(),
  {
    analyticsParams: () => ({}),
  },
);

defineEmits<{
  (e: 'select', attraction: NearbyAttraction): void;
}>();

const { useViewTracker, useClickTracker } = useAnalytics();

const vSightSnippetViewTracker = useViewTracker('attraction-card:sights-snippet.view');
const vSightSnippetClickTracker = useClickTracker('attraction-card:sights-snippet.click');

const thumbnail = computed(
  () => props.attraction.cover_photo?.thumbnail || props.attraction.experience_main_photo_url || '',
);
</script>

<style scoped lang="scss">
.sight-wrap {
  display: block;
  height: 263px;
  text-decoration: none;
  cursor: pointer;
  transform: none;
  transition: all 0.2s;

  &:hover {
    .sight__img {
      transform: scale(110%);
      transition: all 0.2s ease-out;

      @include tablet {
        transform: none;
      }
    }
  }

  @include mobile {
    transform: none !important;
  }
}

.sight {
  position: relative;
  display: flex;
  align-items: flex-end;
  height: 100%;
  overflow: hidden;
  text-decoration: none;
  border-radius: 8px;
  transform: translateZ(0);

  &::before {
    position: absolute;
    inset: 0;
    z-index: 3;
    content: '';
    background: linear-gradient(0.33deg, var(--global-color-neutral-black) 0.27%, transparent 46.03%);
    border-radius: 8px;
  }

  @media only screen and (width <= 767px) {
    margin: 0;
  }
}

.sight__img {
  position: absolute !important;
  z-index: 2;
  width: 100%;
  height: 100%;
  margin: auto;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  transform: none;
  transition: all 0.2s;

  &[title] {
    pointer-events: none;
  }
}

.sight__info {
  position: relative;
  z-index: 4;
  box-sizing: border-box;
  width: 100%;
  padding: 0 16px;
  margin: 0 0 21px;
  color: var(--alias-color-text-inverse);
  text-align: center;
  border-radius: 0 0 8px 8px;
  transition: all 0.2s;
}

.sight__title {
  margin: 0 0 7px;

  @include base-400-300-semi;
}
</style>
